import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import toast from 'react-hot-toast';
import StatusPill from '../components/StatusPill';
import { formatDateTime } from '../lib/dateFormat';
import { Plus, Trash2, X, Bell, MessageCircle, MessageSquare, AlertTriangle } from 'lucide-react';

function RiskBadge({ apptId }) {
  const [r, setR] = React.useState(null);
  React.useEffect(() => {
    api.get(`/api/appointments/${apptId}/risk`).then((x) => setR(x.data)).catch(() => {});
  }, [apptId]);
  if (!r) return null;
  const color = r.bucket === 'red' ? { bg: '#FEE2E2', fg: '#B91C1C', label: 'Hoch' } :
                r.bucket === 'amber' ? { bg: '#FEF3C7', fg: '#B45309', label: 'Mittel' } :
                { bg: '#DCFCE7', fg: '#15803D', label: 'Gering' };
  return (
    <span title={`Vorhersage-Score: ${r.score}% · ${r.explanation?.summary_de || ''}`} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium" style={{ background: color.bg, color: color.fg }} data-testid={`risk-${apptId}`}>
      <AlertTriangle size={10}/> {color.label} {r.score}%
    </span>
  );
}

const RANGES = ['upcoming', 'today', 'past', 'all'];

export default function AppointmentsPage() {
  const { t } = useTranslation();
  const [appts, setAppts] = useState([]);
  const [range, setRange] = useState('upcoming');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = range === 'all' ? {} : { range };
      const r = await api.get('/api/appointments', { params });
      setAppts(r.data.appointments || []);
    } catch (e) {
      toast.error(t('errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [range, t]);

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [load]);

  const fmt = (d) => formatDateTime(d);

  async function setStatus(id, status) {
    try {
      await api.patch(`/api/appointments/${id}/status`, { status });
      load();
    } catch (_) {}
  }

  async function remove(id) {
    if (!window.confirm(t('appointments.deleteConfirm'))) return;
    try {
      await api.delete(`/api/appointments/${id}`);
      load();
    } catch (_) {}
  }

  async function openSlot(appt) {
    if (!window.confirm(t('appointments.openCancelSlotConfirm'))) return;
    try {
      const r = await api.post('/api/slots', {
        starts_at: appt.starts_at,
        duration_min: appt.duration_min || 30,
        source_appointment_id: appt.id,
      });
      const slot = r.data.slot;
      const n = await api.post(`/api/slots/${slot._id}/notify`);
      toast.success(t('slotActivation.notifySuccess', { count: n.data.sent || 0 }));
    } catch (e) {
      toast.error(t('errors.unknown'));
    }
  }

  return (
    <div data-testid="appointments-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('appointments.title')}</h1>
          <p className="mt-1 text-sm text-text-secondary">{t('appointments.subtitle')}</p>
        </div>
        <button onClick={() => setShowForm(true)} className="so-btn so-btn-primary" data-testid="appt-add-btn">
          <Plus size={16} /> {t('appointments.addBtn')}
        </button>
      </div>

      <div className="mt-4 flex items-center gap-1 rounded-full border border-border bg-white p-1 w-fit">
        {RANGES.map((r) => (
          <button
            key={r}
            onClick={() => setRange(r)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${range === r ? 'bg-navy text-white' : 'text-text-secondary hover:text-text'}`}
            data-testid={`appt-range-${r}`}
          >
            {t(`appointments.filters.${r}`)}
          </button>
        ))}
      </div>

      <div className="so-card mt-4 overflow-hidden" data-testid="appt-table-card">
        {loading ? (
          <div className="p-10 text-center text-text-secondary text-sm">{t('common.loading')}</div>
        ) : appts.length === 0 ? (
          <div className="p-10 text-center" data-testid="appt-empty">
            <p className="text-text font-medium">{t('appointments.emptyTitle')}</p>
            <p className="mt-1 text-sm text-text-secondary">{t('appointments.emptyHint')}</p>
          </div>
        ) : (
          <table className="so-table">
            <thead>
              <tr>
                <th>{t('appointments.table.datetime')}</th>
                <th>{t('appointments.table.patient')}</th>
                <th>{t('appointments.table.phone')}</th>
                <th>{t('appointments.table.channel')}</th>
                <th>{t('appointments.table.status')}</th>
                <th style={{ width: 220 }}>{t('appointments.table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {appts.map((a) => (
                <tr key={a.id} data-testid={`appt-row-${a.id}`}>
                  <td className="font-mono text-xs whitespace-nowrap">{fmt(a.starts_at)}</td>
                  <td className="font-medium">{a.patient_first_name} {a.patient_last_name}</td>
                  <td className="font-mono text-xs">{a.patient_phone}</td>
                  <td>
                    <span className="inline-flex items-center gap-1 text-xs text-text-secondary uppercase">
                      {a.channel === 'sms' ? <MessageSquare size={12}/> : <MessageCircle size={12}/>}
                      {a.channel}
                    </span>
                  </td>
                  <td>
                    <StatusPill status={a.status || 'pending'} label={t(`appointments.status.${a.status || 'pending'}`, { defaultValue: a.status || '' })} />
                    {a.last_response && (
                      <span className="ml-2 text-[11px] text-text-secondary font-mono">[{a.last_response}]</span>
                    )}
                    <div className="mt-1"><RiskBadge apptId={a.id}/></div>
                  </td>
                  <td>
                    <div className="flex items-center gap-2 flex-wrap">
                      {a.status === 'scheduled' && (
                        <button onClick={() => setStatus(a.id, 'confirmed')} className="text-success text-xs hover:underline" data-testid={`appt-confirm-${a.id}`}>{t('appointments.status.confirmed')}</button>
                      )}
                      {a.status !== 'cancelled' && a.status !== 'completed' && (
                        <button onClick={() => setStatus(a.id, 'cancelled')} className="text-danger text-xs hover:underline" data-testid={`appt-cancel-${a.id}`}>{t('appointments.status.cancelled')}</button>
                      )}
                      {a.status === 'cancelled' && (
                        <button onClick={() => openSlot(a)} className="text-accent text-xs hover:underline flex items-center gap-1" data-testid={`appt-open-slot-${a.id}`}>
                          <Bell size={12}/> {t('appointments.openCancelSlot')}
                        </button>
                      )}
                      <button onClick={() => remove(a.id)} className="text-text-secondary hover:text-danger text-xs" aria-label="delete"><Trash2 size={14}/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showForm && <AddApptModal onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function AddApptModal({ onClose, onCreated }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    patient_first_name: '',
    patient_last_name: '',
    patient_phone: '',
    starts_at: '',
    duration_min: 30,
    channel: 'auto',
    note: '',
    whatsapp_consent: false,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); }

  async function submit(e) {
    e.preventDefault();
    const errs = {};
    if (!form.patient_first_name.trim()) errs.patient_first_name = t('waitlist.form.errors.firstName');
    if (!form.patient_last_name.trim()) errs.patient_last_name = t('waitlist.form.errors.lastName');
    if (!/^\+\d{8,15}$/.test(form.patient_phone.trim())) errs.patient_phone = t('waitlist.form.errors.phone');
    if (!form.starts_at) errs.starts_at = t('common.required');
    if (!form.whatsapp_consent) errs.whatsapp_consent = t('appointments.form.consentRequiredError');
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSubmitting(true);
    try {
      await api.post('/api/appointments', {
        patient_first_name: form.patient_first_name.trim(),
        patient_last_name: form.patient_last_name.trim(),
        patient_phone: form.patient_phone.trim(),
        starts_at: new Date(form.starts_at).toISOString(),
        duration_min: Number(form.duration_min) || 30,
        channel: form.channel,
        note: form.note.trim(),
        whatsapp_consent: true,
      });
      toast.success(t('appointments.form.success'));
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.message || t('errors.unknown'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center bg-text/40 backdrop-blur-sm p-4 overflow-y-auto" data-testid="appt-modal">
      <div className="so-card w-full max-w-lg p-6 mt-12">
        <div className="flex items-start justify-between">
          <h2 className="text-lg font-bold">{t('appointments.form.title')}</h2>
          <button onClick={onClose} className="text-text-secondary hover:text-text"><X size={18}/></button>
        </div>
        <form onSubmit={submit} className="mt-5 space-y-4" noValidate>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="so-label">{t('appointments.form.firstName')}</label>
              <input value={form.patient_first_name} onChange={(e) => set('patient_first_name', e.target.value)} className="so-input" aria-invalid={!!errors.patient_first_name} data-testid="appt-first-name" />
              {errors.patient_first_name && <p className="mt-1 text-xs text-danger">{errors.patient_first_name}</p>}
            </div>
            <div>
              <label className="so-label">{t('appointments.form.lastName')}</label>
              <input value={form.patient_last_name} onChange={(e) => set('patient_last_name', e.target.value)} className="so-input" aria-invalid={!!errors.patient_last_name} data-testid="appt-last-name" />
              {errors.patient_last_name && <p className="mt-1 text-xs text-danger">{errors.patient_last_name}</p>}
            </div>
          </div>
          <div>
            <label className="so-label">{t('appointments.form.phone')}</label>
            <input value={form.patient_phone} onChange={(e) => set('patient_phone', e.target.value)} placeholder="+43..." className="so-input font-mono" aria-invalid={!!errors.patient_phone} data-testid="appt-phone" />
            {errors.patient_phone && <p className="mt-1 text-xs text-danger">{errors.patient_phone}</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="so-label">{t('appointments.form.startsAt')}</label>
              <input type="datetime-local" value={form.starts_at} onChange={(e) => set('starts_at', e.target.value)} className="so-input" data-testid="appt-starts-at" />
              {errors.starts_at && <p className="mt-1 text-xs text-danger">{errors.starts_at}</p>}
            </div>
            <div>
              <label className="so-label">{t('appointments.form.duration')}</label>
              <input type="number" min="5" max="480" value={form.duration_min} onChange={(e) => set('duration_min', e.target.value)} className="so-input" data-testid="appt-duration" />
            </div>
          </div>
          <div>
            <label className="so-label">{t('appointments.form.channel')}</label>
            <select value={form.channel} onChange={(e) => set('channel', e.target.value)} className="so-input" data-testid="appt-channel">
              <option value="auto">{t('common.channelOptions.auto')}</option>
              <option value="whatsapp">{t('common.channelOptions.whatsapp')}</option>
              <option value="sms">{t('common.channelOptions.sms')}</option>
            </select>
          </div>
          <div>
            <label className="so-label">{t('appointments.form.note')} <span className="lowercase text-text-secondary">({t('common.optional')})</span></label>
            <textarea value={form.note} onChange={(e) => set('note', e.target.value)} className="so-input" rows={2} />
          </div>
          <label className="flex items-start gap-3 rounded-md border border-border bg-background/60 p-3 cursor-pointer" data-testid="appt-consent-label">
            <input
              type="checkbox"
              checked={form.whatsapp_consent}
              onChange={(e) => set('whatsapp_consent', e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-border text-accent focus:ring-accent"
              data-testid="appt-consent-checkbox"
            />
            <span className="text-sm text-text">
              {t('appointments.form.consentLabel')}
              <span className="block text-xs text-text-secondary mt-1">{t('appointments.form.consentHint')}</span>
            </span>
          </label>
          {errors.whatsapp_consent && <p className="text-xs text-danger" data-testid="appt-consent-error">{errors.whatsapp_consent}</p>}
          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={onClose} className="so-btn so-btn-secondary">{t('common.cancel')}</button>
            <button type="submit" className="so-btn so-btn-primary" disabled={submitting || !form.whatsapp_consent} data-testid="appt-submit">
              {submitting ? t('appointments.form.submitting') : t('appointments.form.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
